import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Modal, Segmented, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import formatNumber from '../../Functions/Traitement/FormatNumber';
import { Fullscreen, FullscreenExit, Print, ZoomIn, ZoomOut } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useAuthUser } from '../../Services/Auth/sessionAuth';
import Utilisateur from '../../Models/Utilisateur';
import i18next from 'i18next';
import formatOrderDate from '../../Functions/Traitement/formatOrderDate';
import useEcheancierPret, { echeancierColumns } from '../../Hooks/useEcheancierPret';
import VersionEcheancierSelect from '../Forms/Pret/VersionEcheancierSelect';
import { EcheancierPretResult } from '../../Models/EcheancierPret';

const VERT = '#116599';
const DOC_HEADER_H = 76;

const InfoRow = ({ label, value }: { label: string; value: any }) => (
    <Box display="flex" justifyContent="space-between" gap={2} padding="4px 0" borderBottom="1px dashed #e2e8ee">
        <span style={{ fontSize: 12, color: '#5a6878' }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </Box>
);

const Panel = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Box flex={1} border="1px solid #e2e8ee" borderRadius="8px" overflow="hidden">
        <Box padding="6px 14px" style={{ background: '#eef4f9', borderBottom: `2px solid ${VERT}` }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: VERT, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</span>
        </Box>
        <Box padding="8px 14px">{children}</Box>
    </Box>
);

interface PrintTabAmortProps {
    open: boolean;
    onClose: () => void;
    /** NUMDOS du prêt ou NUMSIMUL de la simulation */
    numDos?: string;
    /** 'PRET' (ECHEANCE_PRET) ou 'SIMULATION' (ECHEANCE_PRET_SIMULATION) */
    typeEcp?: 'PRET' | 'SIMULATION';
    /** Snapshot des valeurs du formulaire (en-tête du document) */
    form?: any;
    /** Aperçu non persisté : échéancier déjà calculé, affiché tel quel
     *  (aucune recharge depuis la base). */
    preview?: EcheancierPretResult | null;
    /** Bouton "Confirmer" affiché en bas en mode aperçu (ex: enregistrer). */
    onConfirm?: () => void;
    /** Etat de chargement du bouton "Confirmer". */
    confirmLoading?: boolean;
}

/**
 * Aperçu modal du tableau d'amortissement.
 * L'échéancier est rechargé depuis la base (charger(numDos)) à chaque
 * ouverture : jamais de données calculées côté frontend.
 */
const PrintTabAmort = ({ open, onClose, numDos, typeEcp, form, preview, onConfirm, confirmLoading }: PrintTabAmortProps) => {
    const { echeances, totaux, versions, version, loading, charger, chargerLocal, afficherVersion, clear } = useEcheancierPret()
    const [introuvable, setIntrouvable] = useState(false)
    const scrollRef = useRef<HTMLDivElement>(null)
    // USER SESSION
    const auth = useAuthUser();
    const user: Utilisateur = JSON.parse(JSON.stringify(auth()))

    // Options de prévisualisation (écran uniquement)
    const [compact, setCompact] = useState(true)
    const [zoom, setZoom] = useState(100)
    const [showTaxe, setShowTaxe] = useState(true)
    const [showEpargne, setShowEpargne] = useState(true)
    const [fullscreen, setFullscreen] = useState(false)

    useEffect(() => {
        if (!open) {
            clear()
            return
        }
        setIntrouvable(false)
        // Aperçu non persisté : on affiche les lignes reçues, sans lecture base.
        if (preview) {
            chargerLocal(preview)
            return
        }
        if (numDos) {
            charger(numDos, typeEcp).then((result) => {
                if (!result) setIntrouvable(true)
            })
        } else {
            setIntrouvable(true)
        }
    }, [open, numDos, typeEcp, preview])

    // Lignes affichées : échéancier persisté + ligne TOTAL calculée par le backend.
    const dataSource = useMemo(() => {
        const rows: any[] = [...echeances]
        if (rows.length > 0) {
            rows.push({
                "numSeq": "", "dateEcheacnce": "", "capital": "", "amortissement": "",
                "interet": "", "taxe": "", "epargne": "", "anHT": "", "anTTC": ""
            })
            if (totaux) {
                rows.push({
                    "numSeq": "TOTAL ", "dateEcheacnce": "", "capital": "",
                    "amortissement": totaux.totAmortissement, "interet": totaux.totInteret,
                    "taxe": totaux.totTaxe, "epargne": totaux.totEpargne,
                    "anHT": totaux.totAnnuiteHT, "anTTC": totaux.totAnnuiteTTC
                })
            }
        }
        return rows
    }, [echeances, totaux])

    const colonnes = useMemo(() =>
        echeancierColumns
            .filter((c: any) => (showTaxe || c.dataIndex !== 'taxe') && (showEpargne || c.dataIndex !== 'epargne'))
            .map((c: any) => ({
                ...c,
                width: c.dataIndex === 'numSeq' ? 70 : c.dataIndex === 'dateEcheacnce' ? 110 : undefined,
                align: c.dataIndex === 'numSeq' ? 'center' : c.dataIndex === 'dateEcheacnce' ? 'center' : 'right'
            })),
        [showTaxe, showEpargne])

    const commissions = [1, 2, 3, 4, 5]
        .map((i) => ({ code: form?.[`CODOPE${i}`], lib: form?.[`lib_CODOPE${i}`], montant: form?.[`MTNCOM${i}`] }))
        .filter((c) => c.montant != undefined && c.montant != 0)

    const resume = [
        { label: 'Amortissement', value: totaux?.totAmortissement },
        { label: 'Intérêts', value: totaux?.totInteret },
        { label: 'Taxes', value: totaux?.totTaxe },
        { label: 'Épargne', value: totaux?.totEpargne },
        { label: 'Annuités HT', value: totaux?.totAnnuiteHT },
        { label: 'Annuités TTC', value: totaux?.totAnnuiteTTC },
    ].filter((r) => r.value != null)

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={fullscreen ? '100%' : '92%'}
            centered={!fullscreen}
            style={fullscreen ? { top: 0, margin: 0, maxWidth: '100vw', paddingBottom: 0, height: '100vh' } : undefined}
            wrapClassName={fullscreen ? 'pam-fs' : undefined}
            rootClassName={fullscreen ? 'pam-fs-root' : undefined}
            zIndex={1500}
            destroyOnClose
            title={null}
            bodyStyle={fullscreen
                ? { paddingTop: 0, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' } as React.CSSProperties
                : { paddingTop: 8 }}
        >
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .pam-print-area, .pam-print-area * { visibility: visible; }
                    .pam-print-area { position: absolute !important; left: 0; top: 0; width: 100%; }
                    .pam-scroll { max-height: none !important; overflow: visible !important; }
                    .ant-modal-wrap { overflow: visible !important; position: static !important; }
                    .ant-modal { position: static !important; padding: 0 !important; max-width: 100% !important; width: 100% !important; }
                    .ant-modal-content { position: static !important; box-shadow: none !important; }
                    .pam-toolbar { display: none !important; }
                }
                @page { margin: 10mm; }
                .pam-total-row td { font-weight: 700 !important; background: #eef4f9 !important; }
                /* Plein écran : le modal occupe toute la fenêtre.
                   zoom: 1.25 compense le "zoom: 80%" global de :root (App.css),
                   sinon les éléments fixed ne couvrent que ~80% de l'écran réel. */
                .pam-fs-root .ant-modal-mask,
                .pam-fs-root .ant-modal-wrap { zoom: 1.25; }
                .pam-fs-root .ant-modal-wrap { overflow: hidden !important; }
                .pam-fs-root .ant-modal {
                    position: fixed !important;
                    inset: 0 !important;
                    top: 0 !important;
                    left: 0 !important;
                    width: 100% !important;
                    max-width: none !important;
                    height: 100% !important;
                    margin: 0 !important;
                    padding: 0 !important;
                }
                .pam-fs-root .ant-modal-content {
                    height: 100% !important;
                    display: flex !important;
                    flex-direction: column !important;
                    border-radius: 0 !important;
                    box-shadow: none !important;
                }
                .pam-fs-root .ant-modal-body {
                    flex: 1 1 auto !important;
                    min-height: 0 !important;
                    display: flex !important;
                    flex-direction: column !important;
                    overflow: hidden !important;
                }
                .pam-fs-root .pam-scroll { flex: 1 1 auto !important; min-height: 0 !important; max-height: none !important; }
                /* Barre d'outils : hauteurs uniformes */
                .pam-toolbar .ant-btn,
                .pam-toolbar .ant-segmented { display: inline-flex; align-items: center; }
                /* Annule le height:49px global de .ant-checkbox-wrapper (StyleHome.css) */
                .pam-toolbar .ant-checkbox-wrapper { height: auto !important; align-items: center !important; }
                .pam-zoom-group {
                    display: inline-flex;
                    align-items: center;
                    border: 1px solid #d9e2ec;
                    border-radius: 6px;
                    overflow: hidden;
                    background: #fff;
                }
                .pam-zoom-group .ant-btn { border: none; box-shadow: none; }
                .pam-zoom-group .pam-zoom-label {
                    min-width: 44px;
                    text-align: center;
                    font-size: 12px;
                    color: #334155;
                    border-left: 1px solid #d9e2ec;
                    border-right: 1px solid #d9e2ec;
                    padding: 3px 4px;
                    user-select: none;
                }
            `}</style>

            {/* Barre d'outils (non imprimée) */}
            <Box className="pam-toolbar" display="flex" alignItems="center" gap={1.5} flexWrap="wrap"
                sx={{
                    padding: '6px 10px',
                    marginBottom: 1.5,
                    background: '#f7fafc',
                    border: '1px solid #e2e8ee',
                    borderRadius: '8px',
                }}>
                <Typography.Text strong style={{ color: VERT, fontSize: 15, whiteSpace: 'nowrap' }}>
                    Tableau d'amortissement{numDos ? ` — N° ${numDos}` : ''}
                    {preview && <Tag color="orange" style={{ marginLeft: 8 }}>APERÇU — NON ENREGISTRÉ</Tag>}
                </Typography.Text>
                <Box flex={1} />
                <Segmented size="small" value={compact ? 'compact' : 'normal'} onChange={(v) => setCompact(v === 'compact')}
                    options={[{ label: 'Compact', value: 'compact' }, { label: 'Normal', value: 'normal' }]} />
                <div className="pam-zoom-group">
                    <Tooltip title="Zoom arrière"><Button size="small" type="text" icon={<ZoomOut style={{ fontSize: 16 }} />} onClick={() => setZoom((z) => Math.max(70, z - 10))} /></Tooltip>
                    <span className="pam-zoom-label">{zoom}%</span>
                    <Tooltip title="Zoom avant"><Button size="small" type="text" icon={<ZoomIn style={{ fontSize: 16 }} />} onClick={() => setZoom((z) => Math.min(150, z + 10))} /></Tooltip>
                </div>
                <Checkbox checked={showTaxe} onChange={(e) => setShowTaxe(e.target.checked)}><span style={{ fontSize: 12 }}>Taxes</span></Checkbox>
                <Checkbox checked={showEpargne} onChange={(e) => setShowEpargne(e.target.checked)}><span style={{ fontSize: 12 }}>Épargne</span></Checkbox>
                <Tooltip title={fullscreen ? 'Quitter le plein écran' : 'Plein écran'}>
                    <Button size="small" icon={fullscreen ? <FullscreenExit style={{ fontSize: 16 }} /> : <Fullscreen style={{ fontSize: 16 }} />}
                        onClick={() => setFullscreen((f) => !f)} />
                </Tooltip>
                <Button type="primary" size="small" icon={<Print style={{ fontSize: 16 }} />} style={{ background: VERT }}
                    onClick={() => window.print()}>Imprimer</Button>
            </Box>

            {/* Document — zone scrollable du modal */}
            <div ref={scrollRef} className="pam-scroll" style={{ flex: fullscreen ? 1 : undefined, minHeight: 0, maxHeight: fullscreen ? 'none' : '75vh', overflowY: 'auto' }}>
                <div className="pam-print-area" style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top left', width: `${10000 / zoom}%` }}>

                    {/* En-tête document — reste visible au scroll */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" borderBottom={`3px solid ${VERT}`} paddingBottom={1.5}
                        position="sticky" top={0} zIndex={5} style={{ background: '#fff' }}>
                        <Box>
                            <Typography.Title level={4} style={{ margin: 0, color: VERT }}>{form?.titre ?? "TABLEAU D'AMORTISSEMENT"}</Typography.Title>
                            {numDos && <Typography.Text style={{ fontSize: 12, color: '#5a6878' }}>Référence : {numDos}</Typography.Text>}
                        </Box>
                        <Box textAlign="right">
                            <div style={{ fontSize: 11, color: '#5a6878' }}>Édité le {(new Date()).toLocaleDateString(i18next.language)} à {(new Date()).toLocaleTimeString(i18next.language)}</div>
                            <div style={{ fontSize: 11, color: '#5a6878' }}>Par {user.coduti} - {user.nomuti}</div>
                        </Box>
                    </Box>

                    {/* Informations */}
                    <Box display="flex" gap={2} marginY={2.5}>
                        <Panel title="Client">
                            <InfoRow label="Numéro client" value={form?.NUMCLI} />
                            <InfoRow label="Nom" value={form?.lib_NUMCLI} />
                            <InfoRow label="Type de prêt" value={`${form?.TYPEPRET ?? ''} - ${form?.lib_TYPEPRET ?? ''}`} />
                            {(form?.CODGES != undefined && form?.CODGES != "") && <InfoRow label="Gestionnaire" value={`${form?.CODGES} - ${form?.lib_CODGES}`} />}
                        </Panel>
                        <Panel title="Paramètres du prêt">
                            <InfoRow label="Montant" value={`${formatNumber(form?.MONTAN)} XAF`} />
                            <InfoRow label="Taux d'intérêt / Taxe" value={`${form?.TAUXINT} % / ${form?.TAUXTAX} %`} />
                            <InfoRow label="Amortissement" value={`${form?.TYPAMORT ?? ''} - ${form?.lib_TYPAMORT ?? ''}`} />
                            <InfoRow label="Périodicité" value={`${form?.PERIODIC ?? ''} - ${form?.lib_PERIODIC ?? ''}`} />
                            <InfoRow label="Échéances / Différé" value={`${formatNumber(form?.NBRECHE)} / ${formatNumber(form?.NBPERDIF)}`} />
                            <InfoRow label="1ère échéance" value={form?.DAT1ECHE ? formatOrderDate(form?.DAT1ECHE, 'JJ-MM-AAAA') : ''} />
                        </Panel>
                    </Box>

                    {(commissions.length > 0 || (form?.cssionApliq != undefined && form?.cssionApliq != "")) &&
                    <Box marginY={2}>
                        <Panel title="Commissions">
                            {commissions.map((c, i) => <InfoRow key={i} label={`${c.code ?? ''} - ${c.lib ?? ''}`} value={`${formatNumber(c.montant)} XAF`} />)}
                            {(form?.cssionApliq != undefined && form?.cssionApliq != "") &&
                                <InfoRow label="Total commissions appliquées" value={`${formatNumber(form?.cssionApliq)} XAF`} />}
                        </Panel>
                    </Box>}

                    {/* Sélecteur de version — toujours en haut à gauche du tableau */}
                    <Box display="flex" justifyContent="flex-start" marginBottom={1}>
                        <VersionEcheancierSelect
                            versions={versions}
                            version={version}
                            onSelect={(v) => afficherVersion(v)}
                        />
                    </Box>

                    {/* Tableau */}
                    <Spin spinning={loading}>
                        {introuvable ?
                            <Box padding={4} textAlign="center" style={{ color: '#999' }}>
                                Aucun échéancier disponible pour ce dossier.
                            </Box>
                            :
                            <Table size={compact ? 'small' : 'middle'} bordered pagination={false}
                                tableLayout="fixed"
                                sticky={{ offsetHeader: DOC_HEADER_H, getContainer: () => scrollRef.current as HTMLElement }}
                                columns={colonnes} dataSource={dataSource}
                                rowClassName={(_r: any, i: number) => dataSource[i]?.numSeq === 'TOTAL ' ? 'pam-total-row' : ''}
                                style={{ width: '100%', borderRadius: 8, overflow: 'hidden' }} />
                        }
                    </Spin>

                    {/* Résumé des totaux */}
                    {resume.length > 0 &&
                    <Box display="flex" flexWrap="wrap" gap={1.5} marginTop={2} padding="10px 14px" borderRadius="8px" style={{ background: '#eef4f9', border: '1px solid #d7e6de' }}>
                        {resume.map((r, i) => (
                            <Box key={i} textAlign="center" minWidth={90}>
                                <div style={{ fontSize: 10, color: '#5a6878', textTransform: 'uppercase' }}>{r.label}</div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: VERT }}>{formatNumber(r.value ?? 0)}</div>
                            </Box>
                        ))}
                    </Box>}

                    {/* Pied de page */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-end" marginTop={4} paddingTop={2} borderTop="1px solid #e2e8ee">
                        <Box>
                            {(form?.UTIVAL != undefined && form?.UTIVAL != "") &&
                                <div style={{ fontSize: 12 }}>Dossier de crédit validé par : <b>{form?.UTIVAL}</b></div>}
                        </Box>
                        <Box textAlign="center">
                            <div style={{ fontSize: 11, color: '#5a6878', marginBottom: 40 }}>Signature et cachet</div>
                            <div style={{ borderTop: '1px solid #999', width: 180 }} />
                        </Box>
                    </Box>
                </div>
            </div>

            {/* Barre de confirmation (aperçu non enregistré, non imprimée) */}
            {onConfirm &&
            <Box className="pam-toolbar" display="flex" alignItems="center" gap={1.5}
                sx={{
                    padding: '8px 10px',
                    marginTop: 1.5,
                    background: '#f7fafc',
                    border: '1px solid #e2e8ee',
                    borderRadius: '8px',
                }}>
                <Typography.Text style={{ fontSize: 12, color: '#b26a00' }}>
                    Cet échéancier est un aperçu : rien n'a encore été enregistré.
                </Typography.Text>
                <Box flex={1} />
                <Button onClick={onClose}>Annuler</Button>
                <Button type="primary" loading={confirmLoading} style={{ background: VERT }} onClick={onConfirm}>
                    Confirmer et enregistrer
                </Button>
            </Box>}
        </Modal>
    );
};

export default PrintTabAmort;
